import EventEmitter from "eventemitter2";
import ScreenShareApis, {
  ConnectStatus,
  NotifyEvent,
  RecordStatus,
  WifiInfo,
} from "./fetch";
import DebugApis from "../debug";

export type { WifiInfo };

export class ScreenShareRecorder extends EventEmitter {
  private _connecting: boolean;
  private _recording: boolean;
  private _currentWifiInfo: WifiInfo | null;
  private _recordStatusUnsubscribe: (() => void) | null;
  private _connectStatusUnsubscribe: (() => void) | null;
  private _recordDataUnsubscribe: (() => void) | null;
  constructor() {
    super();
    this._connecting = false;
    this._recording = false;
    this._currentWifiInfo = null;
    this._recordStatusUnsubscribe = null;
    this._connectStatusUnsubscribe = null;
    this._recordDataUnsubscribe = null;
  }

  public getCurrentWifiInfo = () => {
    return this._currentWifiInfo;
  };

  public get type() {
    return "screenShare";
  }

  public getpermission = async () => {
    // TODO: 获取权限
    return true;
  };

  /**
   * 开始录制
   * 连接（本地连接或者切网、IP连接）->开始录制
   * @param info
   * @returns
   */
  public start = async (info: WifiInfo) => {
    try {
      // 连接
      if (info.localIp.length === 0 && info.ip.length === 0) {
        const newDeviceName: string = await this._handleConnectByCode(
          info.pinCode
        );
        info.deviceName = newDeviceName;
      } else {
        await this._handleConnect(info, true);
      }
      this._connecting = true;
      this._connectStatusUnsubscribe && this._connectStatusUnsubscribe();
      this._connectStatusUnsubscribe = ScreenShareApis.onConnectStatus(
        this._handleConnectStatus
      );
      this._recordStatusUnsubscribe && this._recordStatusUnsubscribe();
      // 注册各种回调
      this._recordStatusUnsubscribe = ScreenShareApis.onRecordStatus(
        this._handleRecordStatus
      );
      // 连接成功之后，开始录制
      let startRecordConfig;
      try {
        const currIsSyncPlaying = await DebugApis.getSyncPlaying();
        startRecordConfig = { syncPlaying: currIsSyncPlaying };
      } catch (error) {
        console.log('getSyncPlaying error', error);
      }
      const res = await ScreenShareApis.startRecord(startRecordConfig);
      if (res.code !== 0) {
        throw new Error(res.msg);
      }
      this._recordDataUnsubscribe && this._recordDataUnsubscribe();
      this._recordDataUnsubscribe = ScreenShareApis.onRecordData(
        this._handleRecordData
      );
      this._currentWifiInfo = info;
      this._recording = true;
    } catch (error) {
      console.log("启动失败", error);
      console.log("启动失败时连接状态", this._connecting);
      // 连接成功但是调用录制失败，需要断开连接
      if (this._connecting) {
        await this.handleDisconnect();
        this._connecting = false;
      }
      // 清除所有状态
      this._handleResetAllStatus();
      return Promise.reject(error);
    }
  };

  /**
   * 停止录制
   */
  public stop = async () => {
    try {
      if (this._connecting) {
        await this.handleDisconnect();
      }
      this._connecting = false;
      this._handleResetAllStatus();
    } catch (error) {
      console.log("screen share stop error", error);
    }
  };

  /**
   * 暂停录制
   */
  public pause = () => {
    if (this._recording) {
      this._recording = false;
    }
  };

  /**
   * 继续录制
   */
  public continue = () => {
    this._recording = true;
  };

  /**
   * 切换设备
   * @param info
   */
  public switchDevice = async (info: WifiInfo) => {
    const preInfo = this._currentWifiInfo;
    try {
      await this.stop();
      await this.start(info);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.log("切换设备失败，重新连接上台设备");
      console.log("要切换的设备", info);
      console.log("被切换的设备", preInfo);
      this.start(preInfo!).catch((error) => {
        this._handleError(error);
      });
      return Promise.reject(error?.message || "切换设备失败");
    }
  };

  /**
   * 连接设备
   * @param info, code
   * @returns
   */
  public connectDevice = async (info?: WifiInfo, pinCode?: string) => {
    try {
      if (info) {
        await this._handleConnect(info, true);
        this._connecting = true;
        this._connectStatusUnsubscribe = ScreenShareApis.onConnectStatus(
          this._handleConnectStatus
        );
        this._currentWifiInfo = info;
      } else if (pinCode && pinCode.length > 0) {
        await this._handleConnectByCode(pinCode);
        this._connecting = true;
        this._connectStatusUnsubscribe = ScreenShareApis.onConnectStatus(
          this._handleConnectStatus
        );
      } else {
        throw Error('连接参数有误');
      }
    } catch (error) {
      console.log("连接失败", error);
      console.log("连接失败时连接状态", this._connecting);
      // 连接成功但是调用录制失败，需要断开连接
      if (this._connecting) {
        await this.handleDisconnect();
        this._connecting = false;
      }
      // 清除所有状态
      this._handleResetAllStatus();
      return Promise.reject(error);
    }
  }

  // needSwitchNetword：本地连接失败时是否需要进行热点连接
  private _handleConnect = async (info: WifiInfo, needSwitchNetword: boolean) => {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      try {
        // 本地连接
        const { code, data } = await ScreenShareApis.connectByLocal(info);
        if (code !== 0 && needSwitchNetword) {
          // 本地连接失败，切换网络
          const switchNetworkRes = await ScreenShareApis.switchNetwork(info);
          if (switchNetworkRes.code !== 0) {
            console.warn("切换网络连接失败, code = ", switchNetworkRes.code);
            throw new Error("设备连接失败，请稍后重试");
          }
          // 网络切换成功之后，使用热点连接
          const { code: connectCode } = await ScreenShareApis.connectByHotspot(
            info
          );
          if (connectCode !== 0) {
            throw new Error("通过host连接失败");
          }
        } else if (!data) {
          throw new Error("连接失败");
        }
        resolve(void 0);
      } catch (error) {
        reject(error);
      }
    });
  };

  private _handleConnectByCode: (pinCode: string) => Promise<string> = async (
    pinCode: string
  ) => {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      try {
        const { code } = await ScreenShareApis.connectByPinCode(pinCode);
        if (code !== 0) {
          console.warn("通过连接码直连失败, code = ", code);
          throw new Error("设备连接失败，请稍后重试");
        }
        // 获取下已连接的设备
        const res = await ScreenShareApis.getCurrDeviceName();
        resolve(res.data as string);
      } catch (error) {
        reject(error);
      }
    });
  };

  private _handleConnectStatus = async (event: NotifyEvent<ConnectStatus>) => {
    console.log("_handleConnectStatus", event);
    if (event.data.status === "disconnected") {
      const error = new Error("设备连接中断");
      this.emit("deviceDisconnected", error);
      this._connecting = false;
      this._handleResetAllStatus();
    }
  };

  public handleDisconnect = async () => {
    if (this._connecting === false) return Promise.resolve();
    let timeout: NodeJS.Timeout;
    let connectStatusUnsubscribe: (() => void) | null;
    return new Promise((resolve, reject) => {
      // 清除副作用
      function clearSideEffect() {
        timeout && clearTimeout(timeout);
        connectStatusUnsubscribe && connectStatusUnsubscribe();
        connectStatusUnsubscribe = null;
      }

      try {
        // 5s超时
        timeout = setTimeout(() => {
          clearSideEffect();
          reject("断开连接超时");
        }, 1000 * 5);
        this._connectStatusUnsubscribe?.();
        // 监听断开连接状态，只有返回disconnected才算真正断开
        connectStatusUnsubscribe = ScreenShareApis.onConnectStatus((event) => {
          if (event.data.status === "disconnected") {
            clearSideEffect();
            console.log("关闭连接成功");
            resolve(void 0);
          }
        });
        console.log('disconnect 触发');
        ScreenShareApis.disconnect(this._currentWifiInfo!);
      } catch (error) {
        clearSideEffect();
        reject(error);
      }
    });
  };

  private _handleRecordData = (data: { data: number[] }) => {
    if (!this._recording) return;
    this.emit("data", data.data);
  };

  private _handleRecordStatus = (event: NotifyEvent<RecordStatus>) => {
    console.log("_handleRecordStatus", event);
    const { status } = event.data;
    if (status === "error") {
      this._handleError(new Error("收到录制状态错误"));
    }
  };

  private _handleResetAllStatus = () => {
    this._recordStatusUnsubscribe?.();
    this._connectStatusUnsubscribe?.();
    this._recordDataUnsubscribe?.();
    this._currentWifiInfo = null;
    this._recordStatusUnsubscribe = null;
    this._connectStatusUnsubscribe = null;
    this._recordDataUnsubscribe = null;
  };

  private _handleError = (error: unknown) => {
    console.log("screen share _handleError", error);
    this.emit("error", error);
  };
}
